import React, { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  UserIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ClockIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface UserTargetingProps {
  onChange?: (targeting: any) => void;
}

interface User {
  id: number;
  email: string;
  name?: string;
  created_at: string;
  last_active?: string;
}

const segments = [
  { id: 'all', name: 'All Users', description: 'Send to all registered users', icon: UserGroupIcon },
  { id: 'active_7d', name: 'Active (7 days)', description: 'Users active in last 7 days', icon: ClockIcon },
  { id: 'active_30d', name: 'Active (30 days)', description: 'Users active in last 30 days', icon: CalendarIcon },
  { id: 'new_users', name: 'New Users', description: 'Users joined in last 7 days', icon: UserIcon },
  { id: 'inactive', name: 'Inactive Users', description: 'Users inactive for 30+ days', icon: FunnelIcon },
];

export default function UserTargeting({ onChange }: UserTargetingProps) {
  const [targetType, setTargetType] = useState<'all' | 'segment' | 'specific'>('all');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('');

  // Mock users data (in production, fetch from API)
  useEffect(() => {
    setUsers([
      { id: 1, email: 'user1@example.com', name: 'John Doe', created_at: '2024-01-01', last_active: '2024-01-03' },
      { id: 2, email: 'user2@example.com', name: 'Jane Smith', created_at: '2024-01-02', last_active: '2024-01-03' },
      { id: 3, email: 'user3@example.com', name: 'Bob Wilson', created_at: '2024-01-03', last_active: '2024-01-02' },
      { id: 4, email: 'admin@clarityofconcept.com', name: 'Admin User', created_at: '2024-01-01', last_active: '2024-01-03' },
    ]);
  }, []);

  useEffect(() => {
    const targeting = {
      target_type: targetType,
      segment: targetType === 'segment' ? selectedSegment : null,
      user_ids: targetType === 'specific' ? selectedUsers : null,
      exclude_ids: [],
      test_mode: testMode,
      scheduled_time: scheduleTime || null,
    };
    onChange?.(targeting);
  }, [targetType, selectedSegment, selectedUsers, testMode, scheduleTime, onChange]);

  const toggleUserSelection = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    setSelectedUsers(users.map(u => u.id));
  };

  const deselectAll = () => {
    setSelectedUsers([]);
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getEstimatedCount = () => {
    if (targetType === 'all') return users.length;
    if (targetType === 'specific') return selectedUsers.length;
    if (targetType === 'segment') {
      // Mock segment counts
      switch (selectedSegment) {
        case 'active_7d': return Math.floor(users.length * 0.6);
        case 'active_30d': return Math.floor(users.length * 0.8);
        case 'new_users': return Math.floor(users.length * 0.2);
        case 'inactive': return Math.floor(users.length * 0.2);
        default: return users.length;
      }
    }
    return 0;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          User Targeting
        </h3>

        {/* Target Type Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
          <button
            type="button"
            onClick={() => setTargetType('all')}
            className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
              targetType === 'all'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center sm:flex-col">
              <UserGroupIcon className={`w-6 h-6 sm:w-8 sm:h-8 mr-3 sm:mr-0 sm:mx-auto sm:mb-2 ${
                targetType === 'all' ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <p className={`text-sm font-medium ${
                targetType === 'all' ? 'text-blue-600' : 'text-gray-700 dark:text-gray-300'
              }`}>
                All Users
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTargetType('segment')}
            className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
              targetType === 'segment'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center sm:flex-col">
              <FunnelIcon className={`w-6 h-6 sm:w-8 sm:h-8 mr-3 sm:mr-0 sm:mx-auto sm:mb-2 ${
                targetType === 'segment' ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <p className={`text-sm font-medium ${
                targetType === 'segment' ? 'text-blue-600' : 'text-gray-700 dark:text-gray-300'
              }`}>
                Segment
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTargetType('specific')}
            className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
              targetType === 'specific'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center sm:flex-col">
              <UserIcon className={`w-6 h-6 sm:w-8 sm:h-8 mr-3 sm:mr-0 sm:mx-auto sm:mb-2 ${
                targetType === 'specific' ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <p className={`text-sm font-medium ${
                targetType === 'specific' ? 'text-blue-600' : 'text-gray-700 dark:text-gray-300'
              }`}>
                Specific Users
              </p>
            </div>
          </button>
        </div>

        {/* Segment Selection */}
        {targetType === 'segment' && (
          <div className="space-y-3">
            {segments.map(segment => {
              const Icon = segment.icon;
              return (
                <button
                  type="button"
                  key={segment.id}
                  onClick={() => setSelectedSegment(segment.id)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    selectedSegment === segment.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start">
                    <Icon className={`w-6 h-6 mr-3 ${
                      selectedSegment === segment.id ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <div className="flex-1">
                      <p className={`font-medium ${
                        selectedSegment === segment.id 
                          ? 'text-blue-600' 
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {segment.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {segment.description}
                      </p>
                    </div>
                    {selectedSegment === segment.id && (
                      <CheckIcon className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Specific Users Selection */}
        {targetType === 'specific' && (
          <div>
            <div className="mb-4">
              <div className="relative">
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users by email or name..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedUsers.length} selected
              </span>
              <div className="space-x-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={deselectAll}
                  className="text-sm text-gray-600 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
              {filteredUsers.map(user => (
                <label
                  key={user.id}
                  className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleUserSelection(user.id)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.name || user.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">
                    ID: {user.id}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Additional Options */}
        <div className="mt-6 space-y-4">
          {/* Test Mode */}
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Test Mode (send only to admin for preview)
            </span>
          </label>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Schedule Send (Optional)
            </label>
            <input
              type="datetime-local"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              className="w-full px-3 py-2 text-sm sm:text-base sm:px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Estimated Recipients
              </p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {testMode ? '1' : getEstimatedCount()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {testMode && 'Test mode enabled'}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {scheduleTime && `Scheduled: ${new Date(scheduleTime).toLocaleString()}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}